import React, { useEffect } from "react";
import { Button, Grid, Typography } from "@mui/material";
import { Field, FormikProps, FormikProvider, useFormik } from "formik";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";
import { Map as LeafletMap } from "leaflet";

import { APIError } from "src/types";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { usePoiSchema } from "src/schemas/poi.schema";
import { PoiMutationData } from "src/hooks";
import { MarkerStatus, useDrawMarker } from "src/hooks/useDrawMarker";
import { featureToWKT, wktPolygonToFeature } from "src/utils/geometry.utils";

export type PoiFormData = PoiMutationData;

interface Props {
  map: LeafletMap;
  title: string;
  data: PoiFormData;
  markerStatus: MarkerStatus;
  readOnly?: boolean;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<PoiFormData> | null>;
  onClose: () => void;
  onSubmit: (values: PoiFormData) => void;
}

export function PoiForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = usePoiSchema();

  const {
    marker,
    renderInitialMarker,
    startDrawing,
    stopDrawing,
    stopEditDrawing,
    redrawDrawing,
    editDrawing,
    removeAllDrawings,
  } = useDrawMarker(props.map, "new");

  const initialValues: PoiFormData = {
    name: props.data.name,
    point_frontendSrid: props.data.point_frontendSrid,
  };

  const formik = useFormik({
    innerRef: props.formRef,
    initialValues,
    validationSchema,
    onSubmit: async (values: PoiFormData) => {
      stopEditDrawing();
      return props.onSubmit(values);
    },
  });

  /**
   * Effects
   */
  useEffect(persistDrawingInForm, [marker]);
  useEffect(() => {
    if (props.data.point_frontendSrid) {
      const geometry = wktPolygonToFeature(props.data.point_frontendSrid);
      renderInitialMarker(geometry);
    } else {
      startDrawing();
    }
  }, []);

  /**
   * Functions
   */
  function persistDrawingInForm() {
    if (!marker) {
      formik.setFieldValue("point_frontendSrid", "");
    } else {
      const point_frontendSrid = featureToWKT(marker);
      formik.setFieldValue("point_frontendSrid", point_frontendSrid);
    }
  }

  function handleRedraw() {
    redrawDrawing();
    formik.setTouched({ point_frontendSrid: false }, false);
  }

  function handleClose() {
    stopDrawing();
    stopEditDrawing();
    removeAllDrawings();
    props.onClose();
  }

  return render();

  function render() {
    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography>{props.title}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Field
                component={TextField}
                name="name"
                label={t("label.name")}
                type="text"
                variant="outlined"
                fullWidth
                disabled={props.readOnly}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <Typography variant="h3" gutterBottom>
                {t("label.location")}
              </Typography>
              {!marker && <Typography>{t("label.drawThePoiOnTheMap")}</Typography>}

              {marker && (
                <>
                  <Typography gutterBottom>{t("label.poiIsVisualizedOnTheMap")}</Typography>

                  {!props.readOnly && (
                    <>
                      <Button color="primary" onClick={handleRedraw}>
                        {t("label.redraw")}
                      </Button>
                      <Button color="primary" onClick={editDrawing}>
                        {t("label.edit")}
                      </Button>
                    </>
                  )}
                </>
              )}
            </Grid>

            {props.errors && (
              <Grid item xs={12}>
                <ServerErrors data={props.errors} />
              </Grid>
            )}

            {!props.readOnly && (
              <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                <Button variant="text" onClick={handleClose}>
                  {t("label.cancel")}
                </Button>
                <ButtonLoading
                  isLoading={formik.isSubmitting || props.isLoading}
                  type="submit"
                  color="primary"
                  variant="contained"
                >
                  {t("label.save")}
                </ButtonLoading>
              </ButtonGrid>
            )}
          </Grid>
        </form>
      </FormikProvider>
    );
  }
}
